import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useCallback,
} from 'react'
import { School } from '../models'
import { axiosInstance } from '../helpers'
import { IdentifiableObject } from '../models'

/** Query params for GET /school-list/. Backend may order nearest schools first when `apartment_id` is set. */
export interface GetSchoolListParams {
    apartment_id?: number
    outreach_eligible_only?: boolean
}

interface SchoolListContextType {
    schoolList: School[]
    getSchoolList: (params?: GetSchoolListParams) => Promise<School[]>
    createSchool: (request: CreateSchoolRequest) => Promise<School>
    updateSchool: (request: UpdateSchoolRequest) => Promise<School>
    deleteSchool: (id: number) => Promise<void>
    loading: boolean
}

const SchoolListContext = createContext<SchoolListContextType | undefined>(
    undefined,
)

interface SchoolListProviderProps {
    children: ReactNode
}

export const SchoolListProvider: React.FC<SchoolListProviderProps> = ({
    children,
}) => {
    const [schoolList, setSchoolList] = useState<School[]>([])
    const [loading, setLoading] = useState(false)

    const getSchoolList = useCallback(async (params?: GetSchoolListParams) => {
        setLoading(true)
        try {
            const queryParams: Record<string, number | boolean> = {}
            if (params?.apartment_id != null) {
                queryParams.apartment_id = params.apartment_id
            }
            if (params?.outreach_eligible_only) {
                queryParams.outreach_eligible_only = true
            }
            const res = await axiosInstance.get('/school-list/', {
                params: queryParams,
            })
            const schools: School[] = res.data
            setSchoolList(res.data)
            return schools
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }, [])

    const createSchool = async (request: CreateSchoolRequest) => {
        setLoading(true)
        try {
            const res = await axiosInstance.post('/school-list/', request)
            const school: School = res.data
            setSchoolList([...schoolList, school])
            return school
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const updateSchool = async (request: UpdateSchoolRequest) => {
        setLoading(true)
        try {
            const res = await axiosInstance.patch(
                `/school-list/${request.id}/`,
                request,
            )
            const school: School = res.data
            _insertOrUpdateIdentifiable(school, setSchoolList)
            return school
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const deleteSchool = async (id: number) => {
        setLoading(true)
        try {
            await axiosInstance.delete(`/school-list/${id}/`)
            setSchoolList(schoolList.filter((school) => school.id !== id))
        } catch (error) {
            console.error(error)
            return Promise.reject(error)
        } finally {
            setLoading(false)
        }
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: React.Dispatch<React.SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    const value: SchoolListContextType = {
        schoolList,
        getSchoolList,
        createSchool,
        updateSchool,
        deleteSchool,
        loading,
    }

    return (
        <SchoolListContext.Provider value={value}>
            {children}
        </SchoolListContext.Provider>
    )
}

export const useSchoolList = (): SchoolListContextType => {
    const context = useContext(SchoolListContext)
    if (context === undefined) {
        throw new Error(
            'useSchoolList must be used within a SchoolListProvider',
        )
    }
    return context
}

export interface CreateSchoolRequest {
    name: string
    address: {
        street: string
        city: string
        state: string
        zip_code: string
    }
    ipeds_id: string
    website_url: string
}

export interface UpdateSchoolRequest {
    id: number
    name?: string
    address?: {
        street?: string
        city?: string
        state?: string
        zip_code?: string
    }
    ipeds_id?: string
    website_url?: string
}
